import React from 'react';
import camelCase from 'camelcase';
import { snakeCase } from 'snake-case';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Filters {
  [key: string]: any | undefined,
}

export interface EditFiltersOptions {
  deleteKeys?: string[];
  destinationPathname?: string,
}

export interface UseFiltersResult<T extends Filters> {
  filters: Partial<T>;
  addFilters: (filters: Partial<T>, options?: EditFiltersOptions) => void;
  replaceFilters: (filters: Partial<T>, options?: EditFiltersOptions) => void;
  removeFilter: (key: keyof T | string, destinationPathname?: string) => void;
  removeFilters: (keys: (keyof T)[] | string[], destinationPathname?: string) => void;
}

export type FiltersMapper<T extends Filters> = {
  [key in keyof T]?: (value?: string) => T[key];
};

export default function useFilters<T extends Filters = Filters>(
  mapper?: FiltersMapper<T>,
): UseFiltersResult<T> {
  const navigate = useNavigate();
  const location = useLocation();

  const filters = React.useMemo(() => {
    const params = new URLSearchParams(location.search);

    if (!mapper) {
      const result: Partial<T> = {};
      params.forEach((value, key) => {
        result[camelCase(key) as keyof T] = value as any;
      });
      return result;
    }

    return Object.keys(mapper).reduce((acc, key) => {
      const paramValue = params.get(snakeCase(key));
      const mapFn = mapper[key as keyof T];

      if (mapFn) {
        acc[key as keyof T] = mapFn(paramValue !== null ? paramValue : undefined);
      }

      return acc;
    }, {} as Partial<T>);
  }, [location.search, mapper]);

  const replaceFilters = React.useCallback(
    (newFilters: Partial<T>, options?: EditFiltersOptions) => {
      const currentParams = new URLSearchParams(location.search);

      if (options?.deleteKeys) {
        options.deleteKeys.forEach((key) => {
          currentParams.delete(snakeCase(key));
        });
      }

      Object.entries(newFilters).forEach(([key, value]) => {
        const formattedKey = snakeCase(key);

        if (
          value === undefined
          || value === null
          || value === ''
          || (Array.isArray(value) && value.length === 0)
        ) {
          currentParams.delete(formattedKey);
        } else if (Array.isArray(value)) {
          currentParams.set(formattedKey, value.join(','));
        } else {
          currentParams.set(formattedKey, value.toString());
        }
      });

      const dest = options?.destinationPathname || location.pathname;
      navigate(`${dest}?${currentParams.toString()}`);
    },
    [location.search, location.pathname, navigate],
  );

  const removeFilter = React.useCallback(
    (toDelete: keyof T | string, destinationPathname: string = location.pathname) => {
      const currentParams = new URLSearchParams(location.search);
      currentParams.delete(snakeCase(toDelete as string));

      navigate(`${destinationPathname}?${currentParams.toString()}`);
    },
    [location.search, location.pathname, navigate],
  );

  const removeFilters = React.useCallback(
    (toDelete: (keyof T)[] | string[], destinationPathname: string = location.pathname) => {
      const currentParams = new URLSearchParams(location.search);
      toDelete.forEach((key) => {
        currentParams.delete(snakeCase(key as string));
      });

      navigate(`${destinationPathname}?${currentParams.toString()}`);
    },
    [location.search, location.pathname, navigate],
  );

  return {
    filters,
    addFilters: replaceFilters,
    removeFilter,
    removeFilters,
    replaceFilters,
  };
}
